import type { ShoppingList } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Bell,
  MoreHorizontal,
  Pencil,
  Plus,
  ReceiptText,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { usePricesEnabled } from '@/features/prices/hooks';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FormField } from '@/features/shared/form-field';
import { HouseholdSelect } from '@/features/shared/household-select';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { useLocalLists, useShoppingPreferences } from '@/features/shopping/hooks';
import { listTypeOptions } from '@/features/shopping/list-types';
import { createListLocal } from '@/features/shopping/shopping-queue';
import { describeReminder } from '@/features/shopping/reminder-format';
import { queryKeys } from '@/lib/query-keys';

export function ShoppingListsPage() {
  const { t } = useTranslation();
  const pricesEnabled = usePricesEnabled();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
  const [formList, setFormList] = useState<ShoppingList | null | 'new'>(null);
  const [deleteList, setDeleteList] = useState<ShoppingList | null>(null);
  // The kind of list ("Lebensmittel", "Drogerie" …): a filter once more than one kind exists.
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const lists = useQuery({ queryKey: queryKeys.shopping.all, queryFn: () => api.shopping.list() });
  // Active lists are on the device: they show without a connection, and a list created
  // offline is there before the server knows it.
  const local = useLocalLists();
  const online = useOnlineStatus();
  const fromDevice = !lists.data && tab === 'ACTIVE';
  const items = lists.data
    ? [
        ...(tab === 'ACTIVE'
          ? (local ?? []).filter((l) => l.pending && !lists.data.some((s) => s.id === l.id))
          : []),
        ...lists.data.filter((l) => l.status === tab),
      ]
    : tab === 'ACTIVE'
      ? (local ?? [])
      : [];
  const types = [...new Set(items.map((l) => l.type).filter((x): x is string => !!x))];
  const shown = typeFilter ? items.filter((l) => l.type === typeFilter) : items;
  const loading = lists.isLoading && !(fromDevice && items.length > 0);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.shopping.all });

  const toggleArchive = useMutation({
    mutationFn: (list: ShoppingList) =>
      api.shopping.update(list.id, {
        status: list.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE',
        version: list.version,
      }),
    onSuccess: invalidate,
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.shopping.remove(id),
    onSuccess: () => {
      setDeleteList(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('shopping.title')}
        description={t('shopping.description')}
        actions={
          <>
            {pricesEnabled && (
              <Button asChild variant="outline">
                <Link to="/shopping/receipts" data-testid="shopping-receipts">
                  <ReceiptText aria-hidden /> {t('receipts.title')}
                </Link>
              </Button>
            )}
            <Button onClick={() => setFormList('new')} data-testid="shopping-create">
              <Plus aria-hidden /> {t('shopping.create')}
            </Button>
          </>
        }
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'ACTIVE' | 'ARCHIVED')} className="mb-4">
        <TabsList aria-label={t('shopping.filter')}>
          <TabsTrigger value="ACTIVE">{t('shopping.active')}</TabsTrigger>
          <TabsTrigger value="ARCHIVED">{t('shopping.archived')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {types.length > 0 && (
        <div
          role="group"
          aria-label={t('shopping.types.filter')}
          className="mb-3 flex flex-wrap gap-2"
          data-testid="shopping-type-filter"
        >
          {[null, ...types].map((type) => (
            <Button
              key={type ?? '__all'}
              type="button"
              size="sm"
              variant={typeFilter === type ? 'default' : 'outline'}
              aria-pressed={typeFilter === type}
              onClick={() => setTypeFilter(type)}
            >
              {type ?? t('shopping.types.all')}
            </Button>
          ))}
        </div>
      )}
      {loading && <Spinner label={t('app.loading')} />}
      {lists.error && !fromDevice && (
        <Alert variant="destructive">{describeError(lists.error)}</Alert>
      )}
      {!online && (
        <p className="mb-3 text-xs text-muted-foreground" role="status">
          {t('shopping.offline.lists')}
        </p>
      )}
      {!loading && items.length === 0 && (
        <EmptyState
          illustration={<Illustration name="shopping" />}
          title={t('shopping.emptyTitle')}
          description={t('shopping.emptyBody')}
          action={
            tab === 'ACTIVE' ? (
              <Button onClick={() => setFormList('new')}>{t('shopping.create')}</Button>
            ) : undefined
          }
        />
      )}
      <ul className="space-y-2">
        {shown.map((list) => (
          <li key={list.id}>
            <Card>
              <CardContent className="flex items-center gap-3 p-3 md:p-4">
                <Link
                  to={`/shopping/${list.id}`}
                  className="min-w-0 flex-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="shopping-list-link"
                >
                  <span className="block truncate font-medium">{list.name}</span>
                  <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {t('shopping.progress', {
                        checked: list.checkedCount,
                        total: list.itemCount,
                      })}
                    </span>
                    {list.type && (
                      <Badge variant="secondary" data-testid="shopping-list-type">
                        {list.type}
                      </Badge>
                    )}
                    {list.householdId && (
                      <Badge variant="muted">
                        <Users className="mr-1 size-3" aria-hidden /> {t('shared.household.shared')}
                      </Badge>
                    )}
                    {list.reminder && (
                      <span className="inline-flex items-center gap-1" data-testid="list-reminder">
                        <Bell className="size-3" aria-hidden /> {describeReminder(list.reminder, t)}
                      </span>
                    )}
                  </span>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('shopping.actions', { name: list.name })}
                    >
                      <MoreHorizontal aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setFormList(list)}>
                      <Pencil aria-hidden /> {t('shopping.rename')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => toggleArchive.mutate(list)}>
                      {list.status === 'ACTIVE' ? (
                        <Archive aria-hidden />
                      ) : (
                        <ArchiveRestore aria-hidden />
                      )}
                      {list.status === 'ACTIVE' ? t('shopping.archive') : t('shopping.unarchive')}
                    </DropdownMenuItem>
                    <DropdownMenuItem destructive onSelect={() => setDeleteList(list)}>
                      <Trash2 aria-hidden /> {t('common.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      <ListFormDialog
        open={formList !== null}
        onOpenChange={(o) => !o && setFormList(null)}
        list={formList === 'new' ? null : formList}
        onSaved={(saved, created) => {
          invalidate();
          if (created) navigate(`/shopping/${saved.id}`);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleteList)}
        onOpenChange={(o) => !o && setDeleteList(null)}
        title={t('shopping.deleteTitle')}
        description={deleteList ? t('shopping.deleteBody', { name: deleteList.name }) : undefined}
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteList) remove.mutate(deleteList.id);
        }}
      />
    </PageContainer>
  );
}

function ListFormDialog({
  open,
  onOpenChange,
  list,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ShoppingList | null;
  onSaved: (list: ShoppingList, created: boolean) => void;
}) {
  const { t } = useTranslation();
  const preferences = useShoppingPreferences();
  const typeOptions = listTypeOptions(preferences.listTypes, t);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [initialised, setInitialised] = useState<string | null>(null);
  if (open && initialised !== (list?.id ?? 'new')) {
    setInitialised(list?.id ?? 'new');
    setName(list?.name ?? '');
    setType(list?.type ?? '');
    setHouseholdId(list?.householdId ?? null);
  }
  if (!open && initialised !== null) setInitialised(null);

  const mutation = useMutation({
    mutationFn: () =>
      list
        ? api.shopping.update(list.id, {
            name: name.trim(),
            type: type.trim() || null,
            version: list.version,
          })
        : // Local-first: works offline, the client-generated id makes the create idempotent.
          createListLocal({ name: name.trim(), type: type.trim() || null, householdId }),
    onSuccess: (saved) => {
      onOpenChange(false);
      onSaved(saved, !list);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{list ? t('shopping.rename') : t('shopping.create')}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) mutation.mutate();
          }}
        >
          <FormField label={t('shopping.fields.name')} required>
            <Input
              value={name}
              maxLength={120}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              data-testid="shopping-name"
            />
          </FormField>
          <FormField label={t('shopping.fields.type')} optionalText={t('common.optional')}>
            <>
              <Input
                value={type}
                maxLength={40}
                list="shopping-list-types"
                placeholder={typeOptions[0] ?? ''}
                onChange={(e) => setType(e.target.value)}
                data-testid="shopping-type"
              />
              <datalist id="shopping-list-types">
                {typeOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </>
          </FormField>
          {!list && <HouseholdSelect value={householdId} onChange={setHouseholdId} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={!name.trim()}
              data-testid="shopping-save"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
